import React, { useEffect, useMemo, useState } from 'react'
import { Radio } from 'transfix-ui/components/Radio'
import { makeStyles, Typography, Button } from 'transfix-ui/core'
import { IFacilityReviewStatuses } from 'types/graphqlTypes'
import { CheckOutlineIcon, OpenInNewTabIcon } from 'transfix-icons'
import { brandColors } from 'transfix-ui'
import { FacilitySummary, IFacilitySummaryProps } from '../FacilitySummary'
import { useMatchingFacilitiesQuery } from './graphql/MatchingFacility'
import { getLastRanMessage, getSortedFacilities } from './utils'

export type TAction = 'current' | 'other'

export interface IFacilitySelectorProps {
  shipperFacilityUuid: string
  onChange: (action: TAction, facility: IFacilitySummaryProps['facility'] | null) => void
  defaultFacility?: IFacilitySummaryProps['facility']
  defaultFacilityAction?: TAction
  hasShipmentId: boolean
  shipperApplicationId: string
}

const useFacilitySelectorStyles = makeStyles(theme => ({
  container: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(1),
  },
  facility: {
    marginLeft: 27,
    marginTop: theme.spacing(1.5),
  },
  facilitiesBox: {
    border: `1px solid ${brandColors.coolGray3}`,
    maxHeight: 300,
    overflowY: 'scroll',
    padding: theme.spacing(1, 2),
    margin: theme.spacing(1.5, 0),
  },
  facilityDescription: {
    display: 'inline-flex',
    gap: theme.spacing(0.5),
    alignItems: 'center',
  },
}))

export const FacilitySelector = ({
  defaultFacility,
  defaultFacilityAction = 'current',
  hasShipmentId,
  onChange,
  shipperApplicationId,
  shipperFacilityUuid,
}: IFacilitySelectorProps) => {
  const [action, setAction] = useState<TAction>(defaultFacilityAction)
  const [facility, setFacility] = useState<IFacilitySummaryProps['facility'] | undefined>(
    defaultFacility
  )
  const styles = useFacilitySelectorStyles()

  const { data } = useMatchingFacilitiesQuery({
    variables: { shipperFacilityUuid, shipperApplicationId },
  })
  const { ediMatchingFacilities: unsortedEdiMatchingFacilities = [], facility: currentFacility } =
    data || {}
  const ediMatchingFacilities = useMemo(() => getSortedFacilities(unsortedEdiMatchingFacilities), [
    unsortedEdiMatchingFacilities,
  ])

  const handleActionChange = (newAction: TAction) => () => {
    setAction(newAction)
  }
  const handleFacilityChange = (facility: IFacilitySummaryProps['facility']) => () => {
    setFacility(facility)
  }

  const handleViewAndMergeDuplicates = () => {
    window.open(`${__ROOT__}/facilities?ediDuplicateFacilityUuid=${shipperFacilityUuid}`, '_blank')
  }

  useEffect(() => {
    onChange(action, (action === 'current' ? currentFacility : facility) || null)
  }, [currentFacility, onChange, facility, action])

  return (
    <div className={styles.container}>
      <div>
        <Radio
          label={`Received via EDI (${hasShipmentId ? 'from shipment' : 'new'})`}
          checked={action === 'current'}
          onChange={handleActionChange('current')}
        />
        <div className={styles.facility}>
          {currentFacility && <FacilitySummary facility={currentFacility} />}
        </div>
      </div>
      {ediMatchingFacilities.length > 1 ? (
        <div>
          <Radio
            label='Transfix facilities'
            checked={action === 'other'}
            onChange={handleActionChange('other')}
          />
          {facility && action === 'other' && (
            <div className={styles.facility}>
              <FacilitySummary facility={facility} />
            </div>
          )}
          <div className={styles.facilitiesBox}>
            {ediMatchingFacilities.map(ediFacility => (
              <Radio
                key={ediFacility.uuid}
                label={
                  <div>
                    <Typography variant='body2'>
                      {ediFacility.location?.address}, {ediFacility.location?.city},{' '}
                      {ediFacility.location?.stateCode} {ediFacility.location?.zipcode}
                    </Typography>
                    <div className={styles.facilityDescription}>
                      {ediFacility.reviewStatus === IFacilityReviewStatuses.primary && (
                        <CheckOutlineIcon color='green2' />
                      )}
                      <Typography color='textSecondary' variant='body2' component='span'>
                        {getLastRanMessage(ediFacility.shipperFacilities?.[0])}
                      </Typography>
                    </div>
                  </div>
                }
                checked={facility?.uuid === ediFacility.uuid}
                disabled={action !== 'other'}
                onChange={handleFacilityChange(ediFacility)}
              />
            ))}
          </div>
          <Button
            startIcon={<OpenInNewTabIcon />}
            variant='outlined'
            onClick={handleViewAndMergeDuplicates}>
            View & Merge Duplicates
          </Button>
        </div>
      ) : (
        <div>
          <Radio
            label='Transfix facility profile'
            checked={action === 'other'}
            onChange={() => {
              setAction('other')
              setFacility(ediMatchingFacilities[0])
            }}
          />
          <div className={styles.facility}>
            <FacilitySummary facility={ediMatchingFacilities[0]} />
          </div>
        </div>
      )}
    </div>
  )
}
